import classNames from 'classnames';
import { useContext, useState } from 'react';
import * as WineStyleEndpoint from '../../pages/api/wineStyle/[id]';
import gtag from '../analytics/gtag';
import { createJsonClient } from '../api/json';
import { TrackingContext } from '../generic/TrackingContext';
import type { Facet, FacetValue } from '../model/Facets';
import ProductIcon from '../product/ProductIcon';
import { getImageUrl } from '../sanity/components/SanityImage';
import type { WineStyleProps } from '../sanity/components/WineStyle';
import type { SanityProjectDetails } from '../sanity/infrastructure/ImageUrlBuilderContext';
import preventDefault from '../utils/preventDefault';
import { useAsyncAction } from '../utils/useAsyncAction';
import useOnMount from '../utils/useOnMount';
import * as style from './ProductGuide.module.css';

export interface FacetListProps {
    facet: Facet;
    onClick(query: string): Promise<void>;
}

export default function FacetList({ facet, onClick }: FacetListProps) {
    return (
        <div className={classNames(style.facetList, facet.listClass && style[facet.listClass], { [style.facetListIcons]: facet.icons })}>
            {facet.code === 'styleCategory'
                ? facet.values.map((value) => <FacetWinestyleValue key={value.code} value={value} onFacetValueSelect={onClick} />)
                : facet.values.map((value) => <FacetValue key={value.code} value={value} onFacetValueSelect={onClick} />)}
        </div>
    );
}

interface Props {
    value: FacetValue;
    onFacetValueSelect(query: string): Promise<void> | void;
}

function FacetValue({ value, onFacetValueSelect }: Props) {
    const { filter_type } = useContext(TrackingContext);

    const [onClick, isLoading] = useAsyncAction(async (value: FacetValue) => {
        gtag('event', value.selected ? 'filter_remove' : 'filter', {
            filter_type,
            filter_value: value.name,
        });
        await onFacetValueSelect(value.query);
    });

    return (
        <a href={value.href} className={style.facetValue} onClick={preventDefault(() => onClick(value))} aria-busy={isLoading}>
            {!!value.iconImageClass && <ProductIcon icon={value.iconImageClass} />}
            <span className={style.name}>{value.name}</span>
        </a>
    );
}

function FacetWinestyleValue({ value, onFacetValueSelect }: Props) {
    const { filter_type } = useContext(TrackingContext);

    const [onClick, isBusy] = useAsyncAction(async (value: FacetValue) => {
        gtag('event', value.selected ? 'filter_remove' : 'filter', {
            filter_type,
            filter_value: value.name,
        });
        await onFacetValueSelect(value.query);
    });

    const [wineStyle, setWineStyle] = useState<WineStyleProps>();
    const [projectDetails, setProjectDetails] = useState<SanityProjectDetails>();

    const [load] = useAsyncAction(async () => {
        const client = createJsonClient(WineStyleEndpoint);
        const { wineStyle, dataset, projectId } = await client.getAnonymously([value.code]);
        setWineStyle(wineStyle);
        setProjectDetails({ dataset, projectId });
    });

    useOnMount(load);

    return (
        <a href={value.href} className={style.facetWinestyleValue} onClick={preventDefault(() => onClick(value))} aria-busy={isBusy}>
            <img src={wineStyle && getImageUrl(wineStyle.image!, 105, 1, projectDetails)} />
            <span className={style.name}>{wineStyle?.title ?? value.name}</span>
            <p className={style.description}>{wineStyle?.summary}</p>
        </a>
    );
}
